import { Injectable } from '@angular/core';
import { FilesService } from './files.service';
import { ToastService } from './toast.service';
import { SectionByTool, SectionFileData } from 'src/app/models';
import {
  FileConfig,
  TabSection,
  Tabs,
  ConfigurationEvent,
  FileOptions,
  TabsTool,
  TemplatesTaxonomy,
  TemplatesTaxonomyCheck,
} from 'src/app/models/tool.model';
import { numberRegex } from 'src/app/resources/Constants';
import { ToolType } from 'src/app/resources/tool-utils';

export interface FileUploadContext {
  chunkCount: number;
  isTemplateFill: boolean;
  toDeleteSection: SectionByTool[];
}

export interface GetSelectedConfigOptions {
  templatesTaxonomyList: TemplatesTaxonomy;
  isTemplateFill: boolean;
  isTemplateFillChanged: boolean;
  toDeleteSection: SectionByTool[];
}

@Injectable({ providedIn: 'root' })
export class ExecutionFormService {

  constructor(
    private filesService: FilesService,
    private toastService: ToastService,
  ) {}

  // ---------------------------------------------------------------------------
  // DOM utilities
  // ---------------------------------------------------------------------------

  handleCollapse($event: Event): void {
    const el = ($event.target as HTMLInputElement);
    if (el) {
      if (el.classList.contains('active')) {
        el.classList.remove('active');
      } else {
        el.classList.add('active');
      }
    }
  }

  handleClass(section: TabSection): void {
    setTimeout(() => {
      const box = document.getElementById(section.type);
      const button = document.getElementById('button-' + section.type);
      if (box && button) {
        box.classList.add('show');
        button.classList.add('active');
      }
    }, 0);
  }

  // ---------------------------------------------------------------------------
  // Pure helpers
  // ---------------------------------------------------------------------------

  addCurrency(name: string): string {
    return name;
  }

  getToolName(typeExecution: string, index?: number, isDelete?: boolean): string | undefined {
    switch (typeExecution) {
      case ToolType.ESG:
        return isDelete ? 'Delete this set' : (index ? 'Set ' + index : 'Add set');
      case ToolType.LCA:
        return isDelete ? 'Delete this fund' : (index ? 'Fund ' + index : 'Add fund');
      default:
        return undefined;
    }
  }

  returnShowClass(typeExecution: string): boolean | undefined {
    switch (typeExecution) {
      case ToolType.IFRS17_L:
      case ToolType.IFRS17_NL:
      case ToolType.LCA:
      case ToolType.SFR:
        return true;
      default:
        return undefined;
    }
  }

  getTabConfiguration(tabs: TabsTool, selectedKey: string): string {
    let label = '';
    (tabs[selectedKey] as Tabs).tab_configuration.map((item: FileConfig, index: number) => {
      if (item.choosen_value) {
        label += (index !== 0 ? ', ' : '') + item.choosen_value;
      }
    });
    return label;
  }

  updateRR(tabs: TabsTool, selectedKey: string, recoveryRate: string): void {
    tabs[selectedKey].tab_configuration.filter(
      (tab: FileConfig) => tab.name === 'Recovery Rate'
    )[0].choosen_value = recoveryRate;
  }

  /**
   * Patches editable_select values for the active tab so that a previously
   * chosen value that no longer appears in the list is still shown.
   */
  patchTabEditableSelectValues(tabs: TabsTool, selectedKey: string): void {
    if (selectedKey !== '' && tabs[selectedKey]) {
      (tabs[selectedKey] as Tabs).tab_configuration
        .filter(x => x.component_type === 'editable_select')
        .forEach((item) => {
          if (item?.choosen_value !== undefined &&
              item?.values.filter(x => x.id === item?.choosen_value).length < 1) {
            if (item?.choosen_value !== null) {
              const fo: FileOptions = { id: item?.choosen_value, value: item?.choosen_value, isChecked: true };
              item?.values.push(fo);
            }
          }
        });
    }
  }

  /**
   * Same patch applied to top-level configurations array.
   */
  patchConfigurationEditableSelectValues(configurations: FileConfig[]): void {
    if (configurations.length > 0) {
      configurations
        .filter(x => x.component_type === 'editable_select')
        .forEach((item) => {
          if (item?.choosen_value !== undefined &&
              item?.values.filter(x => x.id === item?.choosen_value).length < 1) {
            if (item?.choosen_value !== null) {
              const fo: FileOptions = { id: item?.choosen_value, value: item?.choosen_value, isChecked: true };
              item?.values.push(fo);
            }
          }
        });
    }
  }

  deleteData(el: SectionFileData, section: SectionByTool, updateSubTool: () => void): void {
    if (el.isInvalid) {
      section.data = section.data.filter(s => s.id !== el.id);
    } else {
      section.data = section.data.filter(s => !s.info || s.info.id !== el.info.id);
      updateSubTool();
    }
  }

  // ---------------------------------------------------------------------------
  // API methods
  // ---------------------------------------------------------------------------

  async getSingleTabConfig(
    idExecution: string,
    tabsConfiguration: FileConfig,
    selectedKey: string,
    updateSubTool: () => void,
  ): Promise<void> {
    try {
      const response = await this.filesService.updateSingleTabConfig(
        idExecution,
        tabsConfiguration.name,
        tabsConfiguration.choosen_value,
        selectedKey,
      );
      if (response.status) {
        updateSubTool();
      }
    } catch (error) {
      this.toastService.showError(error.error);
      console.error('error =>', error);
    }
  }

  async addTagPromise(
    idExecution: string,
    value: string,
    name: string,
    tabId: string,
    validate?: boolean,
  ): Promise<{ value: string; validationText: string | undefined }> {
    const regex = RegExp(numberRegex);
    const isValidNumber = validate ? regex.test(value) : true;
    if (isValidNumber) {
      try {
        await this.filesService.updateSingleTabConfig(
          idExecution,
          name,
          value.replace('.', ','),
          tabId,
        );
      } catch (error) {
        this.toastService.showError(error.error);
        console.error('error =>', error);
      }
      return { value, validationText: undefined };
    } else {
      return { value, validationText: 'The value entered for the Recovery Rate does not a valid number' };
    }
  }

  async getSelectedConfig(
    idExecution: string,
    event: ConfigurationEvent,
    opts: GetSelectedConfigOptions,
    updateSubTool: () => void,
  ): Promise<{ isTemplateFillChanged: boolean }> {
    let { isTemplateFillChanged } = opts;
    try {
      if (event && event.fileId) {
        await this.filesService.changeSingleFileConfig(event.fileId, event.configName, event.configValue);
        updateSubTool();
      } else {
        if (event.configValue) {
          const response = await this.filesService.updateSingleGeneralConfig(
            idExecution,
            event.configName,
            event.configValue,
            event.configName === 'Template' && opts.templatesTaxonomyList[event.configValue]
              ? opts.templatesTaxonomyList[event.configValue]
              : undefined,
          );
          if (response.status) {
            updateSubTool();
            if (event.configName === 'Template' && !opts.isTemplateFill && !isTemplateFillChanged) {
              opts.toDeleteSection.map(section => {
                section.data = section.data.filter(file => !file.isInvalid);
                return section;
              });
              isTemplateFillChanged = true;
            }
          }
        }
      }
    } catch (error) {
      this.toastService.showError(error.error);
      console.error('error =>', error);
    }
    return { isTemplateFillChanged };
  }

  async checkToggle(
    idExecution: string,
    tabs: TabsTool,
    selectedKey: string,
    value: FileConfig,
    boxIndex: number,
    sectionIndex: number,
    updateSubTool: () => void,
  ): Promise<void> {
    const toggleValue = value.chosen_value;
    const tabSection = (tabs[selectedKey] as Tabs).sections[boxIndex];
    tabSection.section_configurations[sectionIndex].chosen_value = !toggleValue;
    try {
      await this.filesService.updateSingleTabConfig(
        idExecution,
        value.name,
        (!toggleValue).toString(),
        tabSection.section_id,
      );
      updateSubTool();
    } catch (error) {
      tabSection.section_configurations[sectionIndex].chosen_value = toggleValue;
      console.error('error =>', error.error);
      this.toastService.showError(error.error);
    }
  }

  async getSectionConfigValue(
    idExecution: string,
    selectedValue: FileOptions,
    selectedConfig: FileConfig,
  ): Promise<void> {
    try {
      selectedConfig.file_categories.map(item => {
        if (item.data.length) {
          item.data.map(async file => {
            await this.filesService.deleteSingleFile(file.info.id);
          });
        }
      });
      const res = await this.filesService.updateGranularityFiles(idExecution, selectedConfig.name, selectedValue.value);
      selectedConfig.file_categories = res;
    } catch (error) {
      console.error('error =>', error.error);
      this.toastService.showError(error.error);
    }
  }

  /**
   * Upload files, split into 20 MB chunks. Mutates `ctx` (chunkCount, isTemplateFill,
   * toDeleteSection) so the caller can read back updated values.
   * Pass `sfrCustomMode = true` for the SFR send-mail flow.
   */
  async uploadFiles(
    event: FileList,
    section: SectionByTool,
    sectionListType: string,
    idExecution: string,
    selectedKey: string,
    tabs: TabsTool,
    typeExecution: string,
    ctx: FileUploadContext,
    updateSubTool: () => void,
    sfrCustomMode = false,
  ): Promise<void> {
    const list: SectionFileData[] = [];
    for (let i = 0; i < event.length; i++) {
      const file = event.item(i);
      ctx.chunkCount++;
      list.push({ file, id: ctx.chunkCount });
      section.data.unshift({ file, id: ctx.chunkCount });
    }
    try {
      await Promise.all(
        list.map(async (item: SectionFileData) => {
          await this.filesService
            .getFile(idExecution, section.type, item.file, selectedKey, sectionListType, sfrCustomMode)
            .then(
              (responseAll) => {
                const index = section.data.findIndex(el => el.id === item.id);
                const res = responseAll.filter(response => response && (
                  response.id || response.status === 412 || response.status === 400
                ))[0];

                if (res === undefined || res.status === 412 || !res.id) {
                  ctx.isTemplateFill = res.status === 412 ? false : true;
                  this.toastService.showError({ status: String(res.error?.status ?? res.status), detail: res.error?.message ?? 'An unexpected error occurred.' });
                  console.error('error =>', res);
                  ctx.toDeleteSection.push(section);
                  section.data[index].isInvalid = true;
                  section.data[index].error = res.error.message;
                  section.data[index].status = res.status;
                } else {
                  section.data[index] = {
                    info: res,
                    file: item.file,
                    isInvalid: false,
                    show: 'show',
                  };
                }
              },
              (onreject) => { throw onreject; },
            )
            .catch(err => {
              const index = section.data.findIndex(el => el.id === item.id);
              ctx.isTemplateFill = err.status === 412 ? false : true;
              this.toastService.showError(err.error ? err.error : err.statusText);
              ctx.toDeleteSection.push(section);
              section.data[index].isInvalid = true;
              section.data[index].error = err.error;
              section.data[index].status = err.status;
            });

          updateSubTool();

          if (tabs[selectedKey].tab_configuration && typeExecution === ToolType.ESG) {
            const recoveryRateIndex = tabs[selectedKey].tab_configuration
              .findIndex(element => element.name === 'Recovery Rate');
            if (recoveryRateIndex !== -1) {
              const simpleKey = tabs[selectedKey].tab_configuration[recoveryRateIndex].choosen_value?.toString();
              if (simpleKey) {
                try {
                  const response = await this.filesService.updateSingleTabConfig(
                    idExecution,
                    tabs[selectedKey].tab_configuration[recoveryRateIndex].name,
                    simpleKey.replace('.', ','),
                    selectedKey,
                  );
                  if (response.status) {
                    updateSubTool();
                  }
                } catch (error) {
                  this.toastService.showError(error.error);
                  console.error('error =>', error);
                }
              }
            }
          }
        }),
      ).catch(el => console.log(el));
    } catch (error) {
      this.toastService.showError(error.error);
      console.error('error =>', error);
    }
  }

  async sendConfigurationDataToBE(
    idExecution: string,
    selectedValue: FileOptions,
    selectedConfig: FileConfig,
  ): Promise<void> {
    selectedConfig.chosen_value = selectedValue.value;
    await this.filesService.sendConfiguration(idExecution, selectedConfig);
  }

  async sendConfigurationDataValueNameToBE(
    idExecution: string,
    selectedConfig: FileConfig,
  ): Promise<void> {
    selectedConfig.loading = true;
    try {
      await this.filesService.sendConfiguration(idExecution, selectedConfig);
    } finally {
      selectedConfig.loading = false;
    }
  }
}
